import { useState } from 'react';
import { type SecurityConfig, mockSecurityConfig } from '@/mocks/saSecurity';

interface Props {
  config: SecurityConfig;
}

export default function SecurityConfigPanel({ config }: Props) {
  const [expandedSection, setExpandedSection] = useState<string | null>('session');

  const sections = [
    { id: 'session', label: 'Autenticación y Sesiones', icon: 'ri-login-box-line' },
    { id: 'password', label: 'Requisitos de Contraseña', icon: 'ri-key-2-line' },
    { id: 'verification', label: 'Verificación', icon: 'ri-verified-badge-line' },
    { id: 'geo', label: 'Acceso por País', icon: 'ri-earth-line' },
    { id: 'ip', label: 'Reglas de IP', icon: 'ri-computer-line' },
    { id: 'rate', label: 'Rate Limits', icon: 'ri-speed-mini-line' },
  ];

  const toggleSection = (id: string) => {
    setExpandedSection((prev) => (prev === id ? null : id));
  };

  const safeConfig = config || mockSecurityConfig;

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200 bg-background-100/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center">
            <i className="ri-settings-4-line text-background-50 text-sm"></i>
          </div>
          <div>
            <p className="text-foreground-950 text-xs font-bold">Configuración de Seguridad</p>
            <p className="text-foreground-400 text-[10px]">Políticas globales de seguridad de la plataforma</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-background-200">
        {/* Session */}
        <div>
          <button
            onClick={() => toggleSection('session')}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
                <i className="ri-login-box-line text-indigo-500 text-sm"></i>
              </div>
              <span className="text-foreground-800 text-xs font-medium">Autenticación y Sesiones</span>
            </div>
            <i className={expandedSection === 'session' ? 'ri-arrow-up-s-line text-foreground-400 text-sm' : 'ri-arrow-down-s-line text-foreground-400 text-sm'}></i>
          </button>
          {expandedSection === 'session' && (
            <div className="px-4 pb-4 space-y-2">
              <ConfigRow label="Duración de sesión" value={`${safeConfig.sessionDurationMinutes} minutos (${(safeConfig.sessionDurationMinutes / 60).toFixed(0)}h)`} />
              <ConfigRow label="Máximo de sesiones concurrentes" value={`${safeConfig.maxConcurrentSessions} sesiones`} />
              <ConfigRow label="Intentos fallidos máximos" value={`${safeConfig.maxFailedLoginAttempts} intentos`} />
              <ConfigRow label="Duración del bloqueo" value={`${safeConfig.lockoutDurationMinutes} minutos`} />
              <ConfigRow label="Timeout por inactividad" value={`${safeConfig.sessionInactivityTimeoutMinutes} minutos`} />
              <ConfigRow label="Forzar cambio de contraseña" value={`Cada ${safeConfig.forcePasswordResetDays} días`} />
              <ConfigRow label="Recordar sesión" value={safeConfig.rememberMeEnabled ? 'Habilitado' : 'Deshabilitado'} />
            </div>
          )}
        </div>

        {/* Password */}
        <div>
          <button
            onClick={() => toggleSection('password')}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-key-2-line text-amber-500 text-sm"></i>
              </div>
              <span className="text-foreground-800 text-xs font-medium">Requisitos de Contraseña</span>
            </div>
            <i className={expandedSection === 'password' ? 'ri-arrow-up-s-line text-foreground-400 text-sm' : 'ri-arrow-down-s-line text-foreground-400 text-sm'}></i>
          </button>
          {expandedSection === 'password' && (
            <div className="px-4 pb-4 space-y-2">
              <ConfigRow label="Longitud mínima" value={`${safeConfig.passwordMinLength} caracteres`} />
              <ConfigToggle label="Mayúsculas requeridas" value={safeConfig.passwordRequireUppercase} />
              <ConfigToggle label="Números requeridos" value={safeConfig.passwordRequireNumbers} />
              <ConfigToggle label="Símbolos requeridos" value={safeConfig.passwordRequireSymbols} />
            </div>
          )}
        </div>

        {/* Verification */}
        <div>
          <button
            onClick={() => toggleSection('verification')}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                <i className="ri-verified-badge-line text-emerald-500 text-sm"></i>
              </div>
              <span className="text-foreground-800 text-xs font-medium">Verificación</span>
            </div>
            <i className={expandedSection === 'verification' ? 'ri-arrow-up-s-line text-foreground-400 text-sm' : 'ri-arrow-down-s-line text-foreground-400 text-sm'}></i>
          </button>
          {expandedSection === 'verification' && (
            <div className="px-4 pb-4 space-y-2">
              <ConfigToggle label="Verificación de correo requerida" value={safeConfig.requireEmailVerification} />
              <ConfigToggle label="Verificación de teléfono requerida" value={safeConfig.requirePhoneVerification} />
              <ConfigToggle label="2FA habilitado" value={safeConfig.twoFactorEnabled} />
              <ConfigToggle label="2FA obligatorio" value={safeConfig.twoFactorRequired} />
            </div>
          )}
        </div>

        {/* Geo */}
        <div>
          <button
            onClick={() => toggleSection('geo')}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-cyan-50 flex items-center justify-center">
                <i className="ri-earth-line text-cyan-500 text-sm"></i>
              </div>
              <span className="text-foreground-800 text-xs font-medium">Acceso por País</span>
            </div>
            <i className={expandedSection === 'geo' ? 'ri-arrow-up-s-line text-foreground-400 text-sm' : 'ri-arrow-down-s-line text-foreground-400 text-sm'}></i>
          </button>
          {expandedSection === 'geo' && (
            <div className="px-4 pb-4 space-y-2">
              <ConfigRow
                label="Modo de acceso"
                value={safeConfig.countryAccessMode === 'block_list' ? 'Lista de bloqueo' : safeConfig.countryAccessMode === 'allow_list' ? 'Lista de permitidos' : 'Permitir todos'}
              />
              {safeConfig.countryAccessMode === 'block_list' && (
                <div>
                  <p className="text-foreground-400 text-[10px] mb-1">Países bloqueados</p>
                  <div className="flex flex-wrap gap-1">
                    {safeConfig.restrictedCountries.map((c) => (
                      <span key={c} className="px-2 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-medium">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* IP */}
        <div>
          <button
            onClick={() => toggleSection('ip')}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                <i className="ri-computer-line text-slate-500 text-sm"></i>
              </div>
              <span className="text-foreground-800 text-xs font-medium">Reglas de IP</span>
            </div>
            <i className={expandedSection === 'ip' ? 'ri-arrow-up-s-line text-foreground-400 text-sm' : 'ri-arrow-down-s-line text-foreground-400 text-sm'}></i>
          </button>
          {expandedSection === 'ip' && (
            <div className="px-4 pb-4 space-y-2">
              <div>
                <p className="text-foreground-400 text-[10px] mb-1">Lista blanca ({safeConfig.ipWhitelist.length})</p>
                <div className="flex flex-wrap gap-1">
                  {safeConfig.ipWhitelist.map((ip) => (
                    <code key={ip} className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[10px] font-mono">{ip}</code>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-foreground-400 text-[10px] mb-1">Lista negra ({safeConfig.ipBlacklist.length})</p>
                <div className="flex flex-wrap gap-1">
                  {safeConfig.ipBlacklist.map((ip) => (
                    <code key={ip} className="px-2 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-mono">{ip}</code>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Rate Limits */}
        <div>
          <button
            onClick={() => toggleSection('rate')}
            className="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-background-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                <i className="ri-speed-mini-line text-violet-500 text-sm"></i>
              </div>
              <span className="text-foreground-800 text-xs font-medium">Rate Limits por Módulo</span>
            </div>
            <i className={expandedSection === 'rate' ? 'ri-arrow-up-s-line text-foreground-400 text-sm' : 'ri-arrow-down-s-line text-foreground-400 text-sm'}></i>
          </button>
          {expandedSection === 'rate' && (
            <div className="px-4 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="py-2 pr-3 text-foreground-400 text-[10px] font-semibold uppercase">Módulo</th>
                      <th className="py-2 pr-3 text-foreground-400 text-[10px] font-semibold uppercase">Endpoint</th>
                      <th className="py-2 pr-3 text-foreground-400 text-[10px] font-semibold uppercase">Máx req</th>
                      <th className="py-2 pr-3 text-foreground-400 text-[10px] font-semibold uppercase">Ventana</th>
                      <th className="py-2 text-foreground-400 text-[10px] font-semibold uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeConfig.rateLimits.map((rl, i) => (
                      <tr key={i} className="border-b border-background-200/60">
                        <td className="py-2 pr-3">
                          <span className="text-foreground-800 text-[11px] font-medium capitalize">{rl.module.replace('_', ' ')}</span>
                        </td>
                        <td className="py-2 pr-3">
                          <code className="text-foreground-500 text-[10px]">{rl.endpoint}</code>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-foreground-800 text-[11px] font-medium">{rl.maxRequests}</span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-foreground-500 text-[11px]">{rl.windowSeconds}s</span>
                        </td>
                        <td className="py-2">
                          {rl.enabled ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-medium">
                              <i className="ri-check-line text-[9px]"></i> Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-foreground-400 text-[10px] font-medium">
                              <i className="ri-close-line text-[9px]"></i> Inactivo
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-background-100">
      <span className="text-foreground-500 text-[11px]">{label}</span>
      <span className="text-foreground-800 text-[11px] font-medium">{value}</span>
    </div>
  );
}

function ConfigToggle({ label, value }: { label: string; value: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-background-100">
      <span className="text-foreground-500 text-[11px]">{label}</span>
      {value ? (
        <span className="text-emerald-600 text-[11px] font-medium flex items-center gap-1">
          <i className="ri-check-line text-[9px]"></i> Sí
        </span>
      ) : (
        <span className="text-foreground-400 text-[11px] font-medium flex items-center gap-1">
          <i className="ri-close-line text-[9px]"></i> No
        </span>
      )}
    </div>
  );
}